// onlineSession — a module-level holder for the ACTIVE online PlayTable (RemoteTable), mirroring the
// existing studio convention of module singletons + CustomEvents instead of threading props through the
// play surface (SoloMahjong takes no props; see App.tsx seam header).
//
// The lobby (ui/OnlineLobby.tsx) builds a RemoteTable from the matchmaker ticket and calls enterOnline();
// App navigates to 'play'; SoloMahjong reads useOnlineSession() and, when a table is present, drives the
// board from it (instead of constructing a LocalTable) and hides all local-only setup UI. Leaving the
// table (退出 / gameOver) calls leaveOnline(), which disposes the socket and clears the holder.
import { useSyncExternalStore } from 'react';
import type { PlayTable } from '../play/playTable';

let current: PlayTable | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** Install `table` as the active online session, disposing any previous one. Notifies subscribers. */
export function enterOnline(table: PlayTable): void {
  if (current && current !== table) current.dispose();
  current = table;
  emit();
}

/** Tear down the active online session (closes the socket) and clear the holder. No-op if none. */
export function leaveOnline(): void {
  if (!current) return;
  const t = current;
  current = null;
  t.dispose();
  emit();
}

export function getOnlineTable(): PlayTable | null {
  return current;
}

/** Screens the single online-exit teardown may navigate to (mirrors App's `Screen`, minus 'play'). */
export type ExitTo = 'title' | 'online' | 'studio' | 'settings';

/**
 * THE single guarded teardown for leaving an online table (clusters A/D). EVERY online exit — logo, top-nav
 * 工房/設定/対局, HUD ☰ items, WaitingRoom/board 退出 — must route through this so the socket is never
 * orphaned and no ghost local table is spawned on return. No-op (just navigate) when not in an online session.
 *
 *  - If a match is in progress (`phase === 'playing'`) and `confirm` is provided, ask first; cancel aborts.
 *    Waiting/gameOver lobbies leave with no confirmation.
 *  - On commit: `table.leave()` sends `{t:'leave'}` + closes the socket, `leaveOnline()` clears the holder,
 *    then we navigate (default: the main online lobby) via the `mj:navigate` CustomEvent App listens for.
 *  Returns true if the exit proceeded, false if the confirm was cancelled.
 */
export function exitOnline(to: ExitTo = 'online', confirm?: (msg: string) => boolean): boolean {
  const t = current;
  if (t) {
    const remote = t as PlayTable & { phase?: string; leave?: () => void };
    const playing = remote.phase === 'playing';
    if (playing && confirm && !confirm('対局を離れますか？ あなたの席はツモ切りになります。')) return false;
    remote.leave?.(); // {t:'leave'} + socket close
    leaveOnline(); // dispose (idempotent) + clear holder
  }
  window.dispatchEvent(new CustomEvent('mj:navigate', { detail: to }));
  return true;
}

export function subscribeOnline(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React binding: re-renders when the active online table changes (enter/leave). */
export function useOnlineSession(): PlayTable | null {
  return useSyncExternalStore(subscribeOnline, getOnlineTable, () => null);
}

/**
 * Reactive phase of the active online table ('waiting' | 'playing' | … ) or null when there is no online
 * session (local solo). Re-subscribes to the table's own change stream so a waiting→playing transition is
 * observed. App uses this to keep the force-landscape OrientationGate OFF while the (portrait) WaitingRoom
 * is shown — the rotation is only for the board, never the lobby card.
 */
export function useOnlinePhase(): string | null {
  const table = useOnlineSession();
  return useSyncExternalStore(
    (cb) => {
      const t = table as (PlayTable & { subscribe?: (cb: () => void) => () => void }) | null;
      return t && typeof t.subscribe === 'function' ? t.subscribe(cb) : () => {};
    },
    () => (table as (PlayTable & { phase?: string }) | null)?.phase ?? null,
    () => null,
  );
}
